import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface SumUpLastTest {
  testedAt: string;
  success: boolean;
  /** Stable code, e.g. `SUMUP_TEST_UNAUTHORIZED`. */
  error?: string;
}

export interface SumUpCredentialsStatus {
  merchantCode: string | null;
  hasApiKey: boolean;
  encryptionAvailable: boolean;
  lastTest: SumUpLastTest | null;
  /** Merchant code, key and a passed test. */
  credentialsReady: boolean;
}

export interface SumUpReader {
  id: string;
  name: string;
  serialNumber: string | null;
  model: string | null;
  createdAt: string;
}

export interface SumUpIntegrationResponse {
  /** False when the cluster has no SumUp affiliate credentials. */
  affiliateConfigured: boolean;
  credentials: SumUpCredentialsStatus;
  readers: SumUpReader[];
  /** The till sends card payments to terminals. */
  enabled: boolean;
}

export interface SumUpCredentialsInput {
  merchantCode: string;
  /** Omit to keep the stored key. */
  apiKey?: string;
}

export interface SumUpTestResponse {
  success: boolean;
  credentials: SumUpCredentialsStatus;
}

export interface SumUpReaderStatus {
  connection: 'online' | 'offline';
  activity:
    | 'idle'
    | 'selecting_tip'
    | 'waiting_for_card'
    | 'waiting_for_pin'
    | 'waiting_for_signature'
    | 'updating_firmware'
    | 'unknown';
  batteryLevel: number | null;
  lastActivityAt: string | null;
}

/** What the till needs: whether terminals are in use, and which ones. */
export interface SumUpTerminalStatus {
  enabled: boolean;
  readers: { id: string; name: string }[];
}

const SUMUP_QUERY_KEY = ['integrations', 'sumup'] as const;

export function useSumUpIntegration(enabled: boolean) {
  return useQuery({
    queryKey: SUMUP_QUERY_KEY,
    queryFn: () => api.get<SumUpIntegrationResponse>('/integrations/sumup'),
    enabled,
  });
}

/** Needs only `payments.take` or `invoices.manage`. */
export function useSumUpTerminalStatus(enabled: boolean) {
  return useQuery({
    queryKey: [...SUMUP_QUERY_KEY, 'status'],
    queryFn: () => api.get<SumUpTerminalStatus>('/integrations/sumup/status'),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSumUpReaderStatus(readerId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...SUMUP_QUERY_KEY, 'readers', readerId, 'status'],
    queryFn: () => api.get<SumUpReaderStatus>(`/integrations/sumup/readers/${encodeURIComponent(readerId)}/status`),
    enabled,
    retry: false,
  });
}

function useSumUpMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUMUP_QUERY_KEY });
    },
  });
}

export function useSaveSumUpCredentials() {
  return useSumUpMutation((input: SumUpCredentialsInput) =>
    api.put<SumUpCredentialsStatus>('/integrations/sumup/credentials', input),
  );
}

export function useTestSumUpCredentials() {
  return useSumUpMutation(() => api.post<SumUpTestResponse>('/integrations/sumup/test', {}));
}

export function useDeleteSumUpCredentials() {
  return useSumUpMutation(() => api.delete<SumUpCredentialsStatus>('/integrations/sumup/credentials'));
}

export function usePairSumUpReader() {
  return useSumUpMutation((input: { pairingCode: string; name: string }) =>
    api.post<{ reader: SumUpReader }>('/integrations/sumup/readers', input),
  );
}

export function useRenameSumUpReader() {
  return useSumUpMutation((input: { id: string; name: string }) =>
    api.patch<{ reader: SumUpReader }>(`/integrations/sumup/readers/${encodeURIComponent(input.id)}`, {
      name: input.name,
    }),
  );
}

export function useRemoveSumUpReader() {
  return useSumUpMutation((id: string) => api.delete<undefined>(`/integrations/sumup/readers/${encodeURIComponent(id)}`));
}
